import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import api from '../../services/api'
import {
  GET_ENTITIES,
  GET_ENTITY,
  POST_ADD_ENTITY,
  PUT_UPDATE_ENTITY,
  DELETE_ENTITY
} from '../../helpers/url_helper'

// Async thunk for fetching all entities
export const fetchEntities = createAsyncThunk(
  'entity/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_ENTITIES)
      return response.data.entities
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch entities'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching single entity
export const fetchEntity = createAsyncThunk(
  'entity/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_ENTITY}/${id}`)
      return response.data.entity
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch entity details'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for adding entity
export const addEntity = createAsyncThunk(
  'entity/add',
  async (entityData, { rejectWithValue }) => {
    try {
      const response = await api.post(POST_ADD_ENTITY, entityData)
      toast.success('Entity added successfully')
      return response.data.entity
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to add entity'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for updating entity
export const updateEntity = createAsyncThunk(
  'entity/update',
  async ({ id, entityData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${PUT_UPDATE_ENTITY}/${id}`, entityData)
      toast.success('Entity updated successfully')
      return response.data.entity
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to update entity'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for deleting entity
export const deleteEntity = createAsyncThunk(
  'entity/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${DELETE_ENTITY}/${id}`)
      toast.success('Entity deleted successfully')
      return id
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete entity'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  entities: [],
  selectedEntity: null,
  loading: false,
  error: null,
}

const entitySlice = createSlice({
  name: 'entity',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSelectedEntity: state => {
      state.selectedEntity = null
    },
  },
  extraReducers: builder => {
    builder
      // Fetch All Entities
      .addCase(fetchEntities.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEntities.fulfilled, (state, action) => {
        state.loading = false
        state.entities = action.payload
      })
      .addCase(fetchEntities.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Single Entity
      .addCase(fetchEntity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEntity.fulfilled, (state, action) => {
        state.loading = false
        state.selectedEntity = action.payload
      })
      .addCase(fetchEntity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Add Entity
      .addCase(addEntity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addEntity.fulfilled, (state, action) => {
        state.loading = false
        state.entities.push(action.payload)
      })
      .addCase(addEntity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update Entity
      .addCase(updateEntity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateEntity.fulfilled, (state, action) => {
        state.loading = false
        const index = state.entities.findIndex(entity => entity.id === action.payload.id)
        if (index !== -1) {
          state.entities[index] = action.payload
        }
      })
      .addCase(updateEntity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Delete Entity
      .addCase(deleteEntity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        state.loading = false
        state.entities = state.entities.filter(entity => entity.id !== action.payload)
      })
      .addCase(deleteEntity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearSelectedEntity } = entitySlice.actions
export default entitySlice.reducer
